import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import i18n from "i18next";
import React, { ReactElement } from "react";
import {
	DeliveryPackageSourcePickingDataGrid,
	DeliveryPackageSourcePickingDataGridProps
} from "src/components/views/erp/delivery/deliveryPackage/picking/DeliveryPackageSourcePickingDataGrid.tsx";
import { CenteredTypography } from "src/components/common/CenteredTypography.tsx";
import {
	DeliveryPackageLinesDataGrid
} from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackageLinesDataGrid.tsx";
import { RefreshableElementRef, useRefreshRef } from "src/utils/useRefreshRef.ts";
import { useInterval } from "src/utils/useInterval.ts";
import { DeliveryPackagesDataGrid } from "../DeliveryPackagesDataGrid.tsx";
import { DeliveryPackageSource } from "src/api/generated/erp/delivery/model/deliveryPackageSource.ts";
import {
	DeliveryPackageWithSourcesView
} from "src/api/generated/erp/db/types/tables/deliveryPackageWithSourcesView.ts";

export interface DeliveryPackageSourcePickingViewProps {
	deliveryPackageSource: DeliveryPackageSource;
	deliverySourceHasAutomaticReservation: boolean;
	reservationView?: ReservationViewConfig;
	revertReservation: DeliveryPackageSourcePickingDataGridProps["revertReservation"];
	purchaseOrderId?: number;
}

export interface ReservationViewConfig {
	title: string;
	hidden?: boolean;
	render: (params: {
		refreshRef: RefreshableElementRef;
		onReservationCompleted: () => Promise<unknown>;
	}) => ReactElement;
}

export const DeliveryPackageSourcePickingView = ({
	deliveryPackageSource,
	deliverySourceHasAutomaticReservation,
	reservationView,
	revertReservation,
	purchaseOrderId,
}: DeliveryPackageSourcePickingViewProps) => {
	const [selectedPackage, setSelectedPackage] = React.useState<DeliveryPackageWithSourcesView | undefined>(undefined);

	const reservableLinesViewRefreshRef = useRefreshRef();
	const pickableLinesViewRefreshRef = useRefreshRef();
	const pickedLinesViewRefreshRef = useRefreshRef();

	useInterval({
		callback: async () => {
			await Promise.all([reservableLinesViewRefreshRef.refresh(), pickableLinesViewRefreshRef.refresh()]);
		},
		intervalMs: 60 * 1000,
		isActive: true,
	});

	return (
		<AavoMosaic
			viewId={"C2FFEDB0721F4F07"}
			layout={{
				type: "column",
				first: {
					type: "panel",
					title: i18n.t("incomplete_packages"),
					content: (
						<DeliveryPackagesDataGrid
							gridId={"C2FB309364222769"}
							addEnabled
							deliveryPackageSource={deliveryPackageSource}
							packageStates={["INITIAL", "COMPLETED"]}
							onSelectedPackageChanged={(selectedPackages) => setSelectedPackage(selectedPackages[0])}
							showSourceDocumentsButton
							showCalculateWeightButton
							stateChangeButtonsLocation={"actionBar"}
							printPackageLabelButtonLocation={"actionBar"}
						/>
					),
				},
				second: {
					type: "tabs",
					items: [
						reservationView != null && (
							{
								type: "panel",
								title: reservationView.title,
								hidden: reservationView.hidden,
								content: reservationView.render({
									refreshRef: reservableLinesViewRefreshRef,
									onReservationCompleted: () => pickableLinesViewRefreshRef.refresh(),
								}),
							}
						),
						{
							type: "panel",
							title: i18n.t("pickable_lines"),
							content: (
								<DeliveryPackageSourcePickingDataGrid
									deliveryPackageSource={deliveryPackageSource}
									deliveryPackage={selectedPackage}
									refreshRef={pickableLinesViewRefreshRef}
									pickedLinesViewRefreshRef={pickedLinesViewRefreshRef}
									reservableLinesViewRefreshRef={reservableLinesViewRefreshRef}
									deliverySourceHasAutomaticReservation={deliverySourceHasAutomaticReservation}
									revertReservation={revertReservation}
									purchaseOrderId={purchaseOrderId}
								/>
							),
						},
						{
							type: "panel",
							title: i18n.t("package_lines"),
							content:
								!selectedPackage ?
									<CenteredTypography>{i18n.t("select_package")}</CenteredTypography>
								:	<DeliveryPackageLinesDataGrid
										key={selectedPackage.deliveryPackageId}
										deliveryPackage={selectedPackage}
										deliveryPackageSource={deliveryPackageSource}
										pickableLinesViewRefreshRef={pickableLinesViewRefreshRef}
										refreshRef={pickedLinesViewRefreshRef}
									/>,
						},
					],
				},
			}}
		/>
	);
};
